import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Globals } from './globals';
import { ConnectedApp, ConnectedAppAuth, ConnectedAppAuthScope, ConnectedAppEndpoint, ConnectedAppItemObject, ConnectedAppResponseHeader } from 'app/models/connected-app';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class ConnectedAppService {

    constructor(private http: HttpClient, private globals: Globals, private formBuilder: FormBuilder) {
    }

    list() {
        return this.http.get<ConnectedApp[]>(`${this.globals.api_base_url}/connected_apps`);
    }

    put(connectedApp: ConnectedApp) {
        return this.http.put<ConnectedApp>(`${this.globals.api_base_url}/connected_app`, connectedApp);
    }

    deleteById(id: number) {
        return this.http.delete(`${this.globals.api_base_url}/connected_app/${id}`);
    }

    getById(id: number) {
        return this.http.get<ConnectedApp>(`${this.globals.api_base_url}/connected_app/${id}`);
    }

    listAuths(connectedAppId: number) {
        return this.http.get<ConnectedAppAuth[]>(`${this.globals.api_base_url}/connected_app/${connectedAppId}/auths`);
    }

    listEndpoints(connectedAppId: number) {
        return this.http.get<ConnectedAppEndpoint[]>(`${this.globals.api_base_url}/connected_app/${connectedAppId}/endpoints`);
    }
    
    addEndpointsToApp(connectedAppId: number, endpoints: ConnectedAppEndpoint[]) {
        return this.http.post(`${this.globals.api_base_url}/connected_app/${connectedAppId}/endpoint`, {endpoints: endpoints});
    }

    getEndpointById(connectedAppId: number, endpointId: number) {
        return this.http.get<ConnectedAppEndpoint>(`${this.globals.api_base_url}/connected_app/${connectedAppId}/endpoint/${endpointId}`);
    }

    isAuthAuthenticated(connectedAppAuth: ConnectedAppAuth, isGlobal: boolean) {
        return this.http.post<boolean>(`${this.globals.api_base_url}/connected_app/auth/is_authenticated/${isGlobal? 'global':'user'}`, connectedAppAuth);
    }

    activateAuthentication(config: any, isGlobal: boolean) {
        return this.http.post<boolean>(`${this.globals.api_base_url}/connected_app/auth/activate_auth/${isGlobal? 'global':'user'}`, config);
    }

    getAuthRedirectUrl(connectedAppAuth: ConnectedAppAuth, isGlobal: boolean) {
        return `${location.protocol}//${location.host}/connected-apps/app/${connectedAppAuth.connected_app_id}/auth/${connectedAppAuth.id}/redirect/${isGlobal? 'global': 'user'}`;
    }

    getScopesFormArray(scopes: ConnectedAppAuthScope[]): FormArray {
        let oauthScopesFormArray: FormArray = this.formBuilder.array([]);
        (scopes || []).forEach((s) => {
            oauthScopesFormArray.push(this.formBuilder.group({
                name: [s.name, Validators.required],
                description: [s.description || '']
            }));
        });
        return oauthScopesFormArray;
    }

    getAuthsFormArray(connectedApp: ConnectedApp): FormArray {
        let authsFormArray: FormArray = this.formBuilder.array([]);
        (connectedApp.auths || []).forEach((auth) => {
            authsFormArray.push(this.formBuilder.group({
                id: [auth.id],
                name: [auth.name, Validators.required],
                type: [auth.type, Validators.required],
                description: [auth.description],
                api_key_param_name: [auth.api_key_param_name, [(c: AbstractControl) => { return (c.parent && c.parent.get('type').value == 'apiKey')? Validators.required(c): null}]],
                api_key_in: [auth.api_key_in, [(c: AbstractControl) => { return (c.parent && c.parent.get('type').value == 'apiKey')? Validators.required(c): null}]],
                oauth2_grant_type: [auth.oauth2_grant_type, [(c: AbstractControl) => { return (c.parent && c.parent.get('type').value == 'oauth2')? Validators.required(c): null}]],
                oauth2_authorization_url: [auth.oauth2_authorization_url, [(c: AbstractControl) => { 
                    return  (c.parent && c.parent.get('type').value == 'oauth2' && 
                            (c.parent.get('oauth2_grant_type').value == 'implicit' || c.parent.get('oauth2_grant_type').value == 'authorizationCode')
                            )? Validators.required(c): null;
                }]],
                oauth2_token_url: [auth.oauth2_token_url, [(c: AbstractControl) => { 
                    return  (c.parent && c.parent.get('type').value == 'oauth2' && 
                            (c.parent.get('oauth2_grant_type').value == 'password' || c.parent.get('oauth2_grant_type').value == 'clientCredentials' || c.parent.get('oauth2_grant_type').value == 'authorizationCode')
                            )? Validators.required(c): null;
                }]],
                oauth2_scopes: this.getScopesFormArray(auth.oauth2_scopes),
                oauth2_client_id: [auth.oauth2_client_id, [(c: AbstractControl) => { 
                    return  (c.parent && c.parent.get('type').value == 'oauth2')? Validators.required(c): null;
                }]],
                oauth2_client_secret: [auth.oauth2_client_secret, [(c: AbstractControl) => { 
                    return  (c.parent && c.parent.get('type').value == 'oauth2' && 
                            (c.parent.get('oauth2_grant_type').value == 'password' || c.parent.get('oauth2_grant_type').value == 'clientCredentials' || c.parent.get('oauth2_grant_type').value == 'authorizationCode')
                            )? Validators.required(c): null;
                }]]
            }));
        });

        return authsFormArray;
    }

    getAuthMethodsFormArray(e: ConnectedAppEndpoint): FormArray {
        let authMethodsFormArray: FormArray = this.formBuilder.array([]);
        (e.auth_methods || []).forEach((a) => {
            let requiredAuthsFormArray: FormArray = this.formBuilder.array([]);
            a.forEach((requiredAuths) => {
                requiredAuthsFormArray.push(this.formBuilder.group({
                    auth_name: [requiredAuths.auth_name, Validators.required],
                    scopes: [requiredAuths.scopes]
                }));
            });
            authMethodsFormArray.push(requiredAuthsFormArray);
        });
        return authMethodsFormArray;
    }

    getPrimitiveTypeFormGroup(config) {
        return this.formBuilder.group({
            type: [config.type, Validators.required],
            format: [config.format],
            description: [config.description],
            default_value: [config.default],
            enum: [config.enum],
            example: [config.example],
            required: [config.required]
        });
    }

    getItemsObjectForm(items: ConnectedAppItemObject): AbstractControl {
        if (!items)
            return null;

        if (items.type == 'array') {
            return this.formBuilder.group({
                type: [items.type, Validators.required],
                format: [items.format],
                items: this.getItemsObjectForm(items.items) || [],
                items_join_separator: [items.items_join_separator],
                default_value: [items.default_value]
            });    
        } else {
            return this.getPrimitiveTypeFormGroup(items);
        }
    }

    getHeadersFormArray(headers: ConnectedAppResponseHeader[]): FormArray {
        let headersFormArray: FormArray = this.formBuilder.array([]);
        (headers || []).forEach((h) => {
            headersFormArray.push(this.formBuilder.group({
                name: [h.name, Validators.required],
                type: [h.type, Validators.required],
                format: [h.format],
                items: this.getItemsObjectForm(h.items) || [],
                items_join_separator: [h.items_join_separator],
                default_value: [h.default_value]
            }));
        });
        return headersFormArray;
    }

    getArrayTypeFormGroup(schema: any) {
        return this.formBuilder.group({
            type: ['array', Validators.required],
            items: this.getSchemaForm(schema.items),
            required: [schema.required]
        });
    }
    getObjectFormGroup(schema: any) {
        let propertiesFormArray: FormArray = this.formBuilder.array([]);
        (schema.properties || []).forEach((p) => {
            propertiesFormArray.push(this.formBuilder.group({
                name: [p.name, Validators.required],
                schema: this.getSchemaForm(p.schema)
            }));
        });

        return this.formBuilder.group({
            type: ['object', Validators.required],
            properties: propertiesFormArray,
            required: [schema.required]
        });
    }

    getSchemaForm(schema: any) {
        if (!schema) {
            return null;
        }

        let type: string = schema.type;
        if (type == 'object') {
            return this.getObjectFormGroup(schema);
        } else if (type == 'array') {
            return this.getArrayTypeFormGroup(schema);
        } else {
            return this.getPrimitiveTypeFormGroup(schema);
        }
    }

    getResponsesFormArray(e: ConnectedAppEndpoint): FormArray {
        let responsesFormArray: FormArray = this.formBuilder.array([]);
        (e.responses || []).forEach((r) => {
            responsesFormArray.push(this.formBuilder.group({
                id: [r.id],
                code: [r.code, Validators.required],
                description: [r.description],
                headers: this.getHeadersFormArray(r.headers),
                schema: this.getSchemaForm(r.schema) || [],
                examples: [r.examples],
            }));
        });
        return responsesFormArray;
    }

    getParametersFormArray(e: ConnectedAppEndpoint): FormArray {
        let parametersFormArray: FormArray = this.formBuilder.array([]);
        (e.parameters || []).forEach((p) => {
            parametersFormArray.push(this.formBuilder.group({
                id: [p.id],
                name: [p.name, Validators.required],
                description: [p.description],
                in: [p.in, Validators.required],
                required: [p.required],
                schema: this.getSchemaForm(p.schema) || [],
                type: [p.type, [(c: AbstractControl) => { return (c.parent && c.parent.get('in').value != 'body')? Validators.required(c): null}]],
                format: [p.format],
                allow_empty_value: [p.allow_empty_value],
                items: this.getItemsObjectForm(p.items) || [],
                items_join_separator: [p.items_join_separator],
                default_value: [p.default_value],
                enum: [p.enum]
            }));

        });
        return parametersFormArray;
    }

    getEndpointsFormArray(connectedApp: ConnectedApp) : FormArray {
        let endpointsFormArray = this.formBuilder.array([]);
        (connectedApp.endpoints || []).forEach((e) => {
            endpointsFormArray.push(this.formBuilder.group({
                id: [e.id],
                url: [e.url, Validators.required],
                http_verb: [e.http_verb, Validators.required],
                short_description: [e.short_description],
                long_description: [e.long_description],
                operation_key: [e.operation_key],
                schemes: [e.schemes],
                accepts: [e.accepts],
                returns: [e.returns, Validators.required],
                tags: [e.tags],
                auth_methods: this.getAuthMethodsFormArray(e),
                responses: this.getResponsesFormArray(e),
                parameters: this.getParametersFormArray(e),
            }));
        });
        
        return endpointsFormArray;
    }
    formGroup(connectedApp: ConnectedApp): FormGroup {
        return this.formBuilder.group({
            id: [connectedApp.id],
            name: [connectedApp.name, Validators.required],
            description: [connectedApp.description],
            schemes: [connectedApp.schemes, Validators.required],
            host: [connectedApp.host, Validators.required],
            base_path: [connectedApp.base_path],
            auths: this.getAuthsFormArray(connectedApp),
            endpoints: this.getEndpointsFormArray(connectedApp)
        });
    }
}